import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AgentService } from '../core/agent/services/agent.service';
import { User } from '../../models/users';
import { CompanyService } from '../../services/company.service';
import { SharedDataService } from '../../services/shared-data.service';
import { SessionMonitorService } from '../../services/session-monitor.service';
import { AuthSelectableCompany, UserValidationService } from '../../services/user-validation.service';
import { WorkspaceService } from '../../services/workspace.service';
import { environment } from '../../environments/enviroment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageSwitcherComponent } from '../../shared/components/language-switcher/language-switcher.component';

interface RestrictedCompany {
  companyName?: string;
  reason?: string;
  message?: string;
}

interface PlatformInfo {
  id: string;
  name: string;
  domain?: string;
  status?: boolean;
}

interface CompanyInfo {
  id: string;
  businessName: string;
  nit?: string;
  email?: string;
  status?: boolean;
}

interface CompanyPlatformInfo {
  id: string;
  companyId: string;
  platformId: string;
  status?: boolean;
  company: CompanyInfo;
  platform?: PlatformInfo;
}

interface OrganizationOption {
  id: string;
  name: string;
  status?: boolean;
}

interface AreaOption {
  id: string;
  name: string;
  active?: boolean;
}

interface WorkspaceOption {
  id: string;
  name: string;
  active?: boolean;
}

interface AllowedCompany {
  id: string;
  userId: string;
  roleId: string;
  status: boolean;
  companyPlatform: CompanyPlatformInfo;
}

@Component({
  selector: 'app-workspace-selection',
  standalone: true,
  imports: [FormsModule, TranslateModule, LanguageSwitcherComponent],
  templateUrl: './workspace-selection.component.html',
  styleUrl: './workspace-selection.component.scss'
})
export class WorkspaceSelectionComponent implements OnInit, OnDestroy {
  companies: AllowedCompany[] = [];
  restrictedCompanies: RestrictedCompany[] = [];
  pendingAuthCompanies: AuthSelectableCompany[] = [];
  organizations: OrganizationOption[] = [];
  areas: AreaOption[] = [];
  workspaces: WorkspaceOption[] = [];

  selectedCompany: AllowedCompany | null = null;
  selectedOrganization: OrganizationOption | null = null;
  selectedArea: AreaOption | null = null;
  selectedWorkspace: WorkspaceOption | null = null;

  searchOrganization = '';
  searchArea = '';
  searchWorkspace = '';

  isLoading = false;
  isTransitioning = false;
  loadingLabel = '';
  transitionLabel = '';
  emptyStateTitle = '';
  emptyStateMessage = '';
  authCompanySelectionMode = false;
  isManualWorkspaceChange = false;

  private readonly destroy$ = new Subject<void>();
  private readonly autoAdvanceDelayMs = 420;
  private readonly workspaceEnterDelayMs = 560;

  constructor(
    private route: ActivatedRoute,
    private agentService: AgentService,
    private companyService: CompanyService,
    private sharedDataService: SharedDataService,
    private sessionMonitorService: SessionMonitorService,
    private userValidationService: UserValidationService,
    private workspaceService: WorkspaceService,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.isManualWorkspaceChange = this.route.snapshot.queryParamMap.get('changeWorkspace') === 'true';

    this.route.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
      if (this.userValidationService.hasPendingCompanySelection() && !this.userValidationService.hasValidAccessToken()) {
        this.authCompanySelectionMode = true;
        this.pendingAuthCompanies = this.userValidationService.getSelectableCompanies();
        this.clearEmptyState();

        if (this.pendingAuthCompanies.length === 0) {
          this.showEmptyState(
            this.translate.instant('ws.empty.authCompaniesExpired.title'),
            this.translate.instant('ws.empty.authCompaniesExpired.msg')
          );
        }
        return;
      }

      this.authCompanySelectionMode = false;
      this.pendingAuthCompanies = [];

      const platformData = data['platform'] || {};
      this.companies = platformData.allowedCompanies ?? [];
      this.restrictedCompanies = platformData.restrictedCompanies ?? [];

      if (this.companies.length === 0) {
        this.showEmptyState(
          this.translate.instant('ws.empty.noCompanies.title'),
          this.restrictedCompanies.length > 0
            ? this.translate.instant('ws.empty.noCompanies.restricted')
            : this.translate.instant('ws.empty.noCompanies.none')
        );
        return;
      }

      this.clearEmptyState();

      const activeCompanyPlatformId = this.userValidationService.getActiveCompanyPlatformId();
      const activeCompany = activeCompanyPlatformId
        ? this.companies.find((company) => String(company.companyPlatform?.id || '').trim() === activeCompanyPlatformId)
        : null;

      if (activeCompany) {
        this.selectCompany(activeCompany);
        return;
      }

      if (this.companies.length === 1) {
        this.selectCompany(this.companies[0]);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectAuthCompany(company: AuthSelectableCompany): void {
    const companyPlatformId = String(company?.companyPlatformId || '').trim();
    if (!companyPlatformId) {
      this.showEmptyState(
        this.translate.instant('ws.empty.authCompanyInvalid.title'),
        this.translate.instant('ws.empty.authCompanyInvalid.msg')
      );
      return;
    }

    this.isLoading = true;
    this.loadingLabel = this.translate.instant('ws.loading.activatingCompany');
    this.clearEmptyState();

    this.userValidationService.selectCompany(companyPlatformId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.loadingLabel = '';

          if ((response as any)?.requiresOtp) {
            localStorage.setItem('worksel', 'pending');
            this.router.navigate(['/otp-auth']);
            return;
          }

          this.finalizeAuthenticatedSelection();
        },
        error: (error) => {
          console.error('Error selecting company:', error);
          this.isLoading = false;
          this.loadingLabel = '';
          this.showEmptyState(
            this.translate.instant('ws.empty.authCompanyError.title'),
            this.translate.instant('ws.empty.authCompanyError.msg')
          );
        }
      });
  }

  selectCompany(company: AllowedCompany): void {
    if (this.shouldSwitchCompany(company)) {
      this.switchAuthenticatedCompany(company);
      return;
    }

    this.activateCompanyContext(company);
  }

  private activateCompanyContext(company: AllowedCompany): void {
    this.selectedCompany = company;
    this.selectedOrganization = null;
    this.selectedArea = null;
    this.selectedWorkspace = null;
    this.organizations = [];
    this.areas = [];
    this.workspaces = [];
    this.searchOrganization = '';
    this.searchArea = '';
    this.searchWorkspace = '';
    this.clearEmptyState();

    const companyPlatformId = String(company.companyPlatform?.id || '').trim();
    if (!companyPlatformId) {
      this.showEmptyState(
        this.translate.instant('ws.empty.companyPlatformInvalid.title'),
        this.translate.instant('ws.empty.companyPlatformInvalid.msg')
      );
      return;
    }

    const userPlatformId = this.getUserPlatformId(company);
    if (!userPlatformId) {
      this.showEmptyState(
        this.translate.instant('ws.empty.assignmentMissingCompany.title'),
        this.translate.instant('ws.empty.assignmentMissingCompany.msg')
      );
      return;
    }

    this.isLoading = true;
    this.loadingLabel = this.translate.instant('ws.loading.organizations');

    this.workspaceService.getOrganizationsByCompanyPlatformId(companyPlatformId, userPlatformId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (organizations) => {
          this.isLoading = false;
          this.loadingLabel = '';
          this.organizations = this.normalizeNamedCollection(organizations);

          if (this.organizations.length === 0) {
            this.showEmptyState(
              this.translate.instant('ws.empty.organizationsNone.title'),
              this.translate.instant('ws.empty.organizationsNone.msg')
            );
            return;
          }

          if (this.organizations.length === 1) {
            void this.autoAdvance(() => this.selectOrganization(this.organizations[0]), this.translate.instant('ws.transition.orgDetected'));
          }
        },
        error: (error) => {
          console.error('Error loading organizations:', error);
          this.isLoading = false;
          this.loadingLabel = '';
          this.organizations = [];

          if (this.organizations.length === 0) {
            this.showEmptyState(
              this.translate.instant('ws.empty.organizationsError.title'),
              this.translate.instant('ws.empty.organizationsError.msg')
            );
            return;
          }

          if (this.organizations.length === 1) {
            void this.autoAdvance(() => this.selectOrganization(this.organizations[0]), this.translate.instant('ws.transition.orgDetected'));
          }
        }
      });
  }

  private shouldSwitchCompany(company: AllowedCompany): boolean {
    if (!this.userValidationService.hasValidAccessToken()) {
      return false;
    }

    const targetCompanyPlatformId = String(company.companyPlatform?.id || '').trim();
    const activeCompanyPlatformId = this.userValidationService.getActiveCompanyPlatformId();

    return !!targetCompanyPlatformId
      && !!activeCompanyPlatformId
      && activeCompanyPlatformId !== targetCompanyPlatformId;
  }

  private switchAuthenticatedCompany(company: AllowedCompany): void {
    const companyPlatformId = String(company.companyPlatform?.id || '').trim();
    if (!companyPlatformId) {
      this.showEmptyState(
        this.translate.instant('ws.empty.switchCompanyInvalid.title'),
        this.translate.instant('ws.empty.switchCompanyInvalid.msg')
      );
      return;
    }

    this.isLoading = true;
    this.loadingLabel = this.translate.instant('ws.loading.switchingCompany');
    this.clearEmptyState();

    this.userValidationService.switchCompany(companyPlatformId, this.userValidationService.getRefreshToken())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.refreshAuthenticatedContextAfterCompanySwitch();
        },
        error: (error) => {
          console.error('Error switching company:', error);
          this.isLoading = false;
          this.loadingLabel = '';
          this.showEmptyState(
            this.translate.instant('ws.empty.switchCompanyError.title'),
            error?.status === 403
              ? this.translate.instant('ws.empty.switchCompanyForbidden.msg')
              : this.translate.instant('ws.empty.switchCompanyError.msg')
          );
          this.loadCompaniesForAuthenticatedSession();
        }
      });
  }

  private refreshAuthenticatedContextAfterCompanySwitch(): void {
    this.selectedCompany = null;
    this.selectedOrganization = null;
    this.selectedArea = null;
    this.selectedWorkspace = null;
    this.organizations = [];
    this.areas = [];
    this.workspaces = [];
    this.searchOrganization = '';
    this.searchArea = '';
    this.searchWorkspace = '';

    this.userValidationService.loadAuthenticatedUserProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          this.sharedDataService.setAgent(user);
          localStorage.setItem('status_agent', user.status || '');
          this.loadCompaniesForAuthenticatedSession();
        },
        error: (error) => {
          console.error('Error loading authenticated profile after company switch:', error);
          this.isLoading = false;
          this.loadingLabel = '';
          this.showEmptyState(
            this.translate.instant('ws.empty.switchIncomplete.title'),
            this.translate.instant('ws.empty.switchIncomplete.msg')
          );
        }
      });
  }

  selectOrganization(organization: OrganizationOption): void {
    this.selectedOrganization = organization;
    this.selectedArea = null;
    this.selectedWorkspace = null;
    this.areas = [];
    this.workspaces = [];
    this.searchArea = '';
    this.searchWorkspace = '';
    this.clearEmptyState();

    const organizationId = String(organization?.id || '').trim();
    if (!organizationId) {
      this.showEmptyState(
        this.translate.instant('ws.empty.organizationInvalid.title'),
        this.translate.instant('ws.empty.organizationInvalid.msg')
      );
      return;
    }

    const userPlatformId = this.getSelectedUserPlatformId();
    if (!userPlatformId) {
      this.showEmptyState(
        this.translate.instant('ws.empty.assignmentMissingActive.title'),
        this.translate.instant('ws.empty.assignmentMissingActive.msg')
      );
      return;
    }

    this.isLoading = true;
    this.loadingLabel = this.translate.instant('ws.loading.areas');

    this.workspaceService.getAreasByOrganizationId(organizationId, userPlatformId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (areas) => {
          this.isLoading = false;
          this.loadingLabel = '';
          this.areas = this.normalizeNamedCollection(areas);

          if (this.areas.length === 0) {
            this.showEmptyState(
              this.translate.instant('ws.empty.areasNone.title'),
              this.translate.instant('ws.empty.areasNone.msg')
            );
            return;
          }

          if (this.areas.length === 1) {
            void this.autoAdvance(() => this.selectArea(this.areas[0]), this.translate.instant('ws.transition.areaDetected'));
          }
        },
        error: (error) => {
          console.error('Error loading areas:', error);
          this.isLoading = false;
          this.loadingLabel = '';
          this.areas = [];

          if (this.areas.length === 0) {
            this.showEmptyState(
              this.translate.instant('ws.empty.areasError.title'),
              this.translate.instant('ws.empty.areasError.msg')
            );
            return;
          }

          if (this.areas.length === 1) {
            void this.autoAdvance(() => this.selectArea(this.areas[0]), this.translate.instant('ws.transition.areaDetected'));
          }
        }
      });
  }

  selectArea(area: AreaOption): void {
    this.selectedArea = area;
    this.selectedWorkspace = null;
    this.workspaces = [];
    this.searchWorkspace = '';
    this.clearEmptyState();

    const areaId = String(area?.id || '').trim();
    if (!areaId) {
      this.showEmptyState(
        this.translate.instant('ws.empty.areaInvalid.title'),
        this.translate.instant('ws.empty.areaInvalid.msg')
      );
      return;
    }

    const userPlatformId = this.getSelectedUserPlatformId();
    if (!userPlatformId) {
      this.showEmptyState(
        this.translate.instant('ws.empty.assignmentMissingActive.title'),
        this.translate.instant('ws.empty.assignmentMissingActive.msg')
      );
      return;
    }

    this.isLoading = true;
    this.loadingLabel = this.translate.instant('ws.loading.workspaces');

    this.workspaceService.getWorkspacesByAreaId(areaId, userPlatformId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (workspaces) => {
          this.isLoading = false;
          this.loadingLabel = '';
          this.workspaces = this.normalizeNamedCollection(workspaces);

          if (this.workspaces.length === 0) {
            this.showEmptyState(
              this.translate.instant('ws.empty.workspacesNone.title'),
              this.translate.instant('ws.empty.workspacesNone.msg')
            );
            return;
          }

          if (this.workspaces.length === 1) {
            void this.autoAdvance(() => this.selectWorkspace(this.workspaces[0]), this.translate.instant('ws.transition.workspaceDetected'));
          }
        },
        error: (error) => {
          console.error('Error loading workspaces:', error);
          this.isLoading = false;
          this.loadingLabel = '';
          this.workspaces = [];

          if (this.workspaces.length === 0) {
            this.showEmptyState(
              this.translate.instant('ws.empty.workspacesError.title'),
              this.translate.instant('ws.empty.workspacesError.msg')
            );
            return;
          }

          if (this.workspaces.length === 1) {
            void this.autoAdvance(() => this.selectWorkspace(this.workspaces[0]), this.translate.instant('ws.transition.workspaceDetected'));
          }
        }
      });
  }

  selectWorkspace(selection: WorkspaceOption): void {
    this.selectedWorkspace = selection;
    this.clearEmptyState();
  }

  async continueToWorkspace(): Promise<void> {
    const selection = this.selectedWorkspace;
    if (!selection) {
      return;
    }

    this.isTransitioning = true;
    this.transitionLabel = this.translate.instant('ws.transition.entering');
    await this.delay(this.workspaceEnterDelayMs);

    const enrichedWorkspace = this.buildPersistedWorkspace(selection);
    const workspaceId = String(enrichedWorkspace['id'] || '').trim();
    const currentUser = this.sharedDataService.getAgent();
    const currentUserEmail = String(currentUser?.email || '').trim();

    if (!workspaceId || !currentUserEmail) {
      this.isTransitioning = false;
      this.transitionLabel = '';
      this.showEmptyState(
        this.translate.instant('ws.empty.enterFailed.title'),
        this.translate.instant('ws.empty.enterFailedResolve.msg')
      );
      return;
    }

    try {
      const workspaceScopedUser = await firstValueFrom(
        this.agentService.getAgentByEmailAndWorkspace(currentUserEmail, workspaceId)
      );

      const updatedUser: User = {
        ...(currentUser || {} as User),
        ...workspaceScopedUser,
        workspace_id: workspaceId
      } as User;

      this.sharedDataService.setAgent(updatedUser);
      localStorage.setItem('status_agent', updatedUser.status || '');
    } catch (error) {
      console.error('Error loading workspace-specific user:', error);
      this.isTransitioning = false;
      this.transitionLabel = '';
      this.showEmptyState(
        this.translate.instant('ws.empty.enterFailed.title'),
        this.translate.instant('ws.empty.enterFailedUser.msg')
      );
      return;
    }

    this.workspaceService.setWorkspace(enrichedWorkspace);

    if (this.workspaceService.getCurrentWorkspace() != null) {
      localStorage.setItem('worksel', 'complete');
      this.router.navigate(['/layout']).then(() => {
        window.location.reload();
      });
    }
  }

  private finalizeAuthenticatedSelection(): void {
    this.userValidationService.loadAuthenticatedUserProfile()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          this.sharedDataService.setAgent(user);
          localStorage.setItem('status_agent', user.status || '');
          localStorage.setItem('worksel', 'pending');
          this.authCompanySelectionMode = false;
          this.pendingAuthCompanies = [];
          this.loadCompaniesForAuthenticatedSession();
        },
        error: (error) => {
          console.error('Error loading authenticated profile after company selection:', error);
          this.showEmptyState(
            this.translate.instant('ws.empty.sessionIncomplete.title'),
            this.translate.instant('ws.empty.sessionIncomplete.msg')
          );
        }
      });
  }

  private loadCompaniesForAuthenticatedSession(): void {
    const userId = this.userValidationService.getAuthenticatedUserId();
    const platformName = this.userValidationService.getAuthenticatedPlatformName() || environment.platformNameOrchGT;

    if (!userId) {
      this.showEmptyState(
        this.translate.instant('ws.empty.sessionInvalid.title'),
        this.translate.instant('ws.empty.sessionInvalid.msg')
      );
      return;
    }

    this.isLoading = true;
    this.loadingLabel = this.translate.instant('ws.loading.companies');

    this.companyService.getCompaniesByUserId(userId, platformName)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const data = result.data as { findAllByUserIdAndPlatformName?: any };
          const rawSelection = data?.findAllByUserIdAndPlatformName;

          if (Array.isArray(rawSelection)) {
            this.companies = rawSelection;
            this.restrictedCompanies = [];
          } else {
            this.companies = rawSelection?.allowedCompanies ?? [];
            this.restrictedCompanies = rawSelection?.restrictedCompanies ?? [];
          }

          this.isLoading = false;
          this.loadingLabel = '';
          this.initializeAuthenticatedWorkspaceSelection();
        },
        error: (error) => {
          console.error('Error loading companies after authentication:', error);
          this.isLoading = false;
          this.loadingLabel = '';
          this.showEmptyState(
            this.translate.instant('ws.empty.companiesLoadError.title'),
            this.translate.instant('ws.empty.companiesLoadError.msg')
          );
        }
      });
  }

  private initializeAuthenticatedWorkspaceSelection(): void {
    this.clearEmptyState();

    if (this.companies.length === 0) {
      this.showEmptyState(
        this.translate.instant('ws.empty.noCompanies.title'),
        this.restrictedCompanies.length > 0
          ? this.translate.instant('ws.empty.noCompanies.restricted')
          : this.translate.instant('ws.empty.noCompanies.none')
      );
      return;
    }

    const activeCompanyPlatformId = this.userValidationService.getActiveCompanyPlatformId();
    const activeCompany = activeCompanyPlatformId
      ? this.companies.find((company) => String(company.companyPlatform?.id || '').trim() === activeCompanyPlatformId)
      : null;

    if (activeCompany) {
      this.selectCompany(activeCompany);
      return;
    }

    if (this.companies.length === 1) {
      this.selectCompany(this.companies[0]);
    }
  }

  resetCompanySelection(): void {
    this.selectedCompany = null;
    this.selectedOrganization = null;
    this.selectedArea = null;
    this.selectedWorkspace = null;
    this.organizations = [];
    this.areas = [];
    this.workspaces = [];
    this.searchOrganization = '';
    this.searchArea = '';
    this.searchWorkspace = '';
    this.clearEmptyState();
  }

  resetOrganizationSelection(): void {
    this.selectedOrganization = null;
    this.selectedArea = null;
    this.selectedWorkspace = null;
    this.areas = [];
    this.workspaces = [];
    this.searchArea = '';
    this.searchWorkspace = '';
    this.clearEmptyState();
  }

  resetAreaSelection(): void {
    this.selectedArea = null;
    this.selectedWorkspace = null;
    this.workspaces = [];
    this.searchWorkspace = '';
    this.clearEmptyState();
  }

  logout(): void {
    if (this.isUiBlocked) {
      return;
    }

    this.sessionMonitorService.markManualLogout();
    this.workspaceService.clearWorkspaceId();
    this.sharedDataService.clearUser();
    this.router.navigate(['/login']);
  }

  get currentTitle(): string {
    if (this.authCompanySelectionMode) {
      return 'Selecciona una empresa';
    }

    if (!this.selectedCompany) {
      return 'Selecciona una compania';
    }

    if (!this.selectedOrganization) {
      return 'Selecciona una organizacion';
    }

    if (!this.selectedArea) {
      return 'Selecciona un area';
    }

    return 'Selecciona un workspace';
  }

  get currentDescription(): string {
    if (this.authCompanySelectionMode) {
      return 'Primero activaremos tu empresa en la sesion para continuar con el contexto operativo.';
    }

    if (this.isManualWorkspaceChange) {
      return 'Selecciona el workspace al que quieres cambiar dentro de tu empresa activa.';
    }

    if (!this.selectedCompany) {
      return 'Usaremos la compania para cargar tu contexto disponible.';
    }

    if (!this.selectedOrganization) {
      return `Compania seleccionada: ${this.selectedCompany.companyPlatform.company.businessName}`;
    }

    if (!this.selectedArea) {
      return `Organizacion seleccionada: ${this.selectedOrganization.name}`;
    }

    return `Area seleccionada: ${this.selectedArea.name}`;
  }

  get currentStep(): number {
    if (this.authCompanySelectionMode) {
      return 1;
    }

    if (!this.selectedCompany) {
      return 1;
    }

    if (!this.selectedOrganization) {
      return 2;
    }

    if (!this.selectedArea) {
      return 3;
    }

    return 4;
  }

  get progressPercent(): number {
    return this.currentStep * 25;
  }

  get currentStageLabel(): string {
    if (this.authCompanySelectionMode) {
      return 'Empresa';
    }

    switch (this.currentStep) {
      case 1:
        return 'Compania';
      case 2:
        return 'Organizacion';
      case 3:
        return 'Area';
      default:
        return 'Workspace';
    }
  }

  get currentOptionsCount(): number {
    if (this.authCompanySelectionMode) {
      return this.pendingAuthCompanies.length;
    }

    switch (this.currentStep) {
      case 1:
        return this.companies.length;
      case 2:
        return this.organizations.length;
      case 3:
        return this.areas.length;
      default:
        return this.workspaces.length;
    }
  }

  get platformName(): string {
    return this.selectedCompany?.companyPlatform?.platform?.name ||
      this.companies[0]?.companyPlatform?.platform?.name ||
      'Mindx Live';
  }

  get selectedCompanyName(): string {
    if (this.authCompanySelectionMode) {
      return 'Pendiente';
    }

    return this.selectedCompany?.companyPlatform?.company?.businessName || 'Sin seleccionar';
  }

  get selectedOrganizationName(): string {
    return this.selectedOrganization?.name || 'Pendiente';
  }

  get selectedAreaName(): string {
    return this.selectedArea?.name || 'Pendiente';
  }

  get selectedWorkspaceName(): string {
    return this.selectedWorkspace?.name || 'Pendiente';
  }

  get isUiBlocked(): boolean {
    return this.isLoading || this.isTransitioning;
  }

  get busyLabel(): string {
    return this.isLoading ? this.loadingLabel : this.transitionLabel;
  }

  isStepComplete(step: number): boolean {
    return this.currentStep > step;
  }

  isStepActive(step: number): boolean {
    return this.currentStep === step;
  }

  get filteredOrganizations(): OrganizationOption[] {
    return this.filterNamedCollection(this.organizations, this.searchOrganization);
  }

  get filteredAreas(): AreaOption[] {
    return this.filterNamedCollection(this.areas, this.searchArea);
  }

  get filteredWorkspaces(): WorkspaceOption[] {
    return this.filterNamedCollection(this.workspaces, this.searchWorkspace);
  }

  get canContinueToWorkspace(): boolean {
    return !!this.selectedWorkspace && !this.isUiBlocked;
  }

  private getSelectedUserPlatformId(): string {
    return this.getUserPlatformId(this.selectedCompany);
  }

  private getUserPlatformId(company: AllowedCompany | null | undefined): string {
    return String(company?.id || '').trim();
  }

  private buildPersistedWorkspace(workspace: WorkspaceOption): Record<string, string | boolean> {
    const selectedCompany = this.selectedCompany;
    const selectedOrganization = this.selectedOrganization;
    const selectedArea = this.selectedArea;
    const companyPlatformId = selectedCompany?.companyPlatform?.id || '';
    const rawPlatformId = String(
      selectedCompany?.companyPlatform?.platformId ||
      selectedCompany?.companyPlatform?.platform?.id ||
      ''
    );
    const normalizedPlatformId = this.normalizePlatformId(rawPlatformId);

    return {
      id: workspace.id,
      workspaceId: workspace.id,
      name: workspace.name,
      workspaceName: workspace.name,
      active: workspace.active ?? true,
      companyPlatformId,
      company_platform_id: companyPlatformId,
      platformId: normalizedPlatformId !== null ? String(normalizedPlatformId) : rawPlatformId,
      platformUuid: rawPlatformId,
      platformNumericId: normalizedPlatformId !== null ? String(normalizedPlatformId) : '',
      userPlatformId: selectedCompany?.id || '',
      companyId: selectedCompany?.companyPlatform?.companyId || '',
      companyName: selectedCompany?.companyPlatform?.company?.businessName || '',
      organizationId: selectedOrganization?.id || '',
      organizationName: selectedOrganization?.name || '',
      areaId: selectedArea?.id || '',
      areaName: selectedArea?.name || '',
      roleId: selectedCompany?.roleId || ''
    };
  }

  private normalizeNamedCollection<T extends { id: string; name: string }>(
    source?: T[] | null
  ): T[] {
    return (Array.isArray(source) ? source : [])
      .filter((item): item is T => !!item?.id && !!item?.name)
      .map(item => ({
        ...item,
        id: String(item.id),
        name: String(item.name).trim()
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }));
  }

  private filterNamedCollection<T extends { name: string }>(items: T[], term: string): T[] {
    const normalizedTerm = String(term || '').trim().toLowerCase();
    if (!normalizedTerm) {
      return items;
    }

    return items.filter(item => String(item.name || '').toLowerCase().includes(normalizedTerm));
  }

  private showEmptyState(title: string, message: string): void {
    this.emptyStateTitle = title;
    this.emptyStateMessage = message;
  }

  private clearEmptyState(): void {
    this.emptyStateTitle = '';
    this.emptyStateMessage = '';
  }

  private normalizePlatformId(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = String(value).trim();
    if (!normalized) {
      return null;
    }

    if (/^\d+$/.test(normalized)) {
      const parsed = Number(normalized);
      return Number.isNaN(parsed) ? null : parsed;
    }

    const platformUuidMatch = normalized.match(/^0{8}-0{4}-4000-8000-0+(\d+)$/i);
    if (platformUuidMatch) {
      const parsed = Number(platformUuidMatch[1]);
      return Number.isNaN(parsed) ? null : parsed;
    }

    return null;
  }

  private async autoAdvance(action: () => void | Promise<void>, label: string): Promise<void> {
    this.isTransitioning = true;
    this.transitionLabel = label;

    try {
      await this.delay(this.autoAdvanceDelayMs);
      if (this.destroy$.closed) {
        return;
      }

      await action();
    } finally {
      this.isTransitioning = false;
      this.transitionLabel = '';
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => {
      window.setTimeout(resolve, ms);
    });
  }
}
